import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { MASTER_BUSINESS_TYPES, getMasterTypeByKey } from '@/lib/businessTypes';

export const Onboarding = () => {
  const [step, setStep] = useState(1);
  // selectedKey holds a MASTER_BUSINESS_TYPES.key (e.g. "erkek_kuafor"),
  // NOT a backend type. Resolved to (backendType, apptCategory, apptSubcat) on save.
  const [selectedKey, setSelectedKey] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  // Check if a business type was already selected from the landing page
  useEffect(() => {
    const savedSelection = JSON.parse(localStorage.getItem('selectedBusinessType') || 'null');
    // Tolerate both legacy {type: 'barber'} and new {key: 'erkek_kuafor'} formats
    const key = savedSelection?.key || savedSelection?.type;
    if (key && getMasterTypeByKey(key)) {
      setSelectedKey(key);
      setStep(2);
    }
  }, []);

  const handleComplete = async () => {
    if (!selectedKey || !businessName.trim()) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    const master = getMasterTypeByKey(selectedKey);
    if (!master) {
      toast.error('Geçersiz işletme türü');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/onboarding/complete`,
        {
          business_type: master.backendType,
          business_name: businessName,
          labels: master.labels,
          appointment_category: master.apptCategory,
          appointment_subcategory: master.apptSubcat,
        },
        { withCredentials: true },
      );

      localStorage.removeItem('selectedBusinessType');
      toast.success('Kurulum tamamlandı!');
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error('Kurulum başarısız');
    }
  };

  const selected = selectedKey ? getMasterTypeByKey(selectedKey) : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] p-6">
      <Card className="w-full max-w-4xl border border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-3xl tracking-tight text-center">
            Hoş Geldiniz! 🎉
          </CardTitle>
          <CardDescription className="text-center text-base">
            {step === 1 ? 'Bu sistemi ne için kullanacaksınız?' : 'İşletme bilgilerinizi girin'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="onboarding-type-grid">
              {MASTER_BUSINESS_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <div
                    key={type.key}
                    onClick={() => { setSelectedKey(type.key); setStep(2); }}
                    data-testid={`onboarding-opt-${type.key}`}
                    className={`p-6 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-lg ${
                      selectedKey === type.key
                        ? 'border-[#2563EB] bg-[#F9F8F6]'
                        : 'border-[#E5E5E5] hover:border-[#2563EB]'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`${type.color} p-3 rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{type.label}</h3>
                        <p className="text-sm text-[#595959]">
                          {type.labels.people} · {type.labels.payment}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-3 p-4 bg-[#F9F8F6] rounded-lg">
                {selected ? (
                  <>
                    <div className={`${selected.color} p-2 rounded-lg`}>
                      <selected.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-medium">{selected.label}</span>
                  </>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessName">İşletme Adı</Label>
                <Input
                  id="businessName"
                  type="text"
                  placeholder="Örn: ABC Sitesi, XYZ Spor Salonu"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="h-11"
                  data-testid="onboarding-name-input"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Geri
                </Button>
                <Button
                  onClick={handleComplete}
                  className="flex-1 bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                  disabled={!businessName.trim()}
                  data-testid="onboarding-submit"
                >
                  Kurulumu Tamamla
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
